import { Info, Settings2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { POPUP_LEVEL } from '@/components/ui/popup-level';
import { useDarkMode, type ThemePref } from '@/hooks/use-dark-mode';
import { useFieldAudit } from '@/hooks/use-field-audit';

const THEMES: ThemePref[] = ['light', 'dark', 'system'];

/**
 * Quick-settings gear + popover — a temporary home for the live-testing toggles (language, theme)
 * that used to sit loose in the top bars. Parking them behind the gear keeps the bar clean and lets
 * them be removed later without a bar redesign. Self-contained: manages its own open state.
 */
export default function QuickSettingsPopover({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useDarkMode();
  const { fieldAudit, setFieldAudit } = useFieldAudit();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!popRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  function toggle() {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    setOpen((o) => !o);
  }

  const size = compact ? 'h-8 w-8' : 'h-9 w-9';
  const seg = 'flex w-full rounded-lg border border-ws-border p-1';
  const segBtn = (active: boolean) =>
    `flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${active ? 'bg-ws-btn-active text-ws-btn-active-fg' : 'text-ws-muted hover:text-ws-text'}`;

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label={t('nav.quickSettings')}
        aria-haspopup='dialog'
        aria-expanded={open}
        className={`flex ${size} items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover ${open ? 'bg-ws-hover text-ws-text' : ''}`}
      >
        <Settings2 className='h-4 w-4' />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          role='dialog'
          aria-label={t('nav.quickSettings')}
          style={{ top: pos.top, right: pos.right }}
          className={`fixed ${POPUP_LEVEL} w-64 rounded-xl border border-ws-border bg-ws-elevated p-4 shadow-xl`}
        >
          <div className='mb-3 flex items-start gap-2 rounded-lg bg-ws-warning-bg/60 px-2.5 py-1.5 text-xs text-ws-warning-fg'>
            <Info className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <span>{t('nav.quickSettingsNote')}</span>
          </div>

          <div className='mb-3'>
            <p className='mb-1.5 text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('profile.settings.language')}</p>
            <div className={seg} role='group' aria-label={t('profile.settings.language')}>
              <button className={segBtn(i18n.language !== 'pt-BR')} onClick={() => i18n.changeLanguage('en')} aria-pressed={i18n.language !== 'pt-BR'}>English</button>
              <button className={segBtn(i18n.language === 'pt-BR')} onClick={() => i18n.changeLanguage('pt-BR')} aria-pressed={i18n.language === 'pt-BR'}>Português</button>
            </div>
          </div>

          <div className='mb-3'>
            <p className='mb-1.5 text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('profile.settings.appearance')}</p>
            <div className={seg} role='group' aria-label={t('profile.settings.appearance')}>
              {THEMES.map((opt) => (
                <button key={opt} className={segBtn(theme === opt)} onClick={() => setTheme(opt)} aria-pressed={theme === opt}>
                  {t(`profile.settings.theme.${opt}`)}
                </button>
              ))}
            </div>
          </div>

          {/* A debug affordance, not a preference: tints every text/number control by its family so
              a mis-filed one, or a raw input that was never migrated (no tint at all), is visible
              without reading the markup. Kept here rather than removed after the migration — it is
              the fastest way to check a NEW block's fields behave like everyone else's. */}
          <div>
            <p className='mb-1.5 text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('nav.fieldAudit')}</p>
            <div className={seg} role='group' aria-label={t('nav.fieldAudit')}>
              <button className={segBtn(!fieldAudit)} onClick={() => setFieldAudit(false)} aria-pressed={!fieldAudit}>{t('nav.fieldAuditOff')}</button>
              <button className={segBtn(fieldAudit)} onClick={() => setFieldAudit(true)} aria-pressed={fieldAudit}>{t('nav.fieldAuditOn')}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
